import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSettings } from "@/context/SettingsContext";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "jb_cart";
const SAVED_KEY = "jb_saved";

const buildItem = (product, variant, custom = {}) => {
  const cname = (custom.name || "").trim();
  const cnum = (custom.number || "").trim();
  const ccolor = custom.color || "Black";
  const garment = variant.options?.garment || "Hoodie";
  const base = process.env.REACT_APP_BACKEND_URL;
  const image = `${base}/api/mockup/${product.id}?color=${encodeURIComponent(ccolor)}&garment=${encodeURIComponent(garment)}`;
  return {
    key: `${product.id}-${variant.id}-${cname}-${cnum}-${ccolor}`,
    product_id: product.id,
    variant_id: variant.id,
    title: product.title,
    size: [variant.options?.garment, variant.options?.size].filter(Boolean).join(" / ") || variant.title,
    price: variant.price,
    image,
    garment: variant.options?.garment || "",
    custom_name: cname,
    custom_number: cnum,
    custom_color: ccolor,
    quantity: 1,
  };
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [saved, setSaved] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(SAVED_KEY, JSON.stringify(saved));
  }, [saved]);

  const addItem = (product, variant, custom = {}) => {
    const item = buildItem(product, variant, custom);
    setItems((prev) => {
      const found = prev.find((i) => i.key === item.key);
      if (found) {
        return prev.map((i) => (i.key === item.key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      return [...prev, item];
    });
    setOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateQty = (key, delta) =>
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i))
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  // ---- Save for later (wishlist) ----
  const addToSaved = (item) =>
    setSaved((prev) => (prev.find((s) => s.key === item.key) ? prev : [...prev, { ...item, quantity: 1 }]));

  const saveItem = (product, variant, custom = {}) => {
    addToSaved(buildItem(product, variant, custom));
  };

  const saveForLater = (key) => {
    setItems((prev) => {
      const it = prev.find((i) => i.key === key);
      if (it) addToSaved(it);
      return prev.filter((i) => i.key !== key);
    });
  };

  const moveToCart = (key) => {
    setSaved((prevSaved) => {
      const it = prevSaved.find((s) => s.key === key);
      if (it) {
        setItems((prev) => {
          const found = prev.find((i) => i.key === it.key);
          if (found) return prev.map((i) => (i.key === it.key ? { ...i, quantity: i.quantity + it.quantity } : i));
          return [...prev, it];
        });
      }
      return prevSaved.filter((s) => s.key !== key);
    });
  };

  const removeSaved = (key) => setSaved((prev) => prev.filter((s) => s.key !== key));

  const count = useMemo(() => items.reduce((n, i) => n + i.quantity, 0), [items]);
  const { discountCents } = useSettings();
  const net = (p) => Math.max((p || 0) - (discountCents || 0), 0);
  const subtotal = useMemo(
    () => items.reduce((n, i) => n + net(i.price) * i.quantity, 0),
    [items, discountCents]
  );
  const originalSubtotal = useMemo(() => items.reduce((n, i) => n + i.price * i.quantity, 0), [items]);
  const savings = originalSubtotal - subtotal;

  return (
    <CartContext.Provider
      value={{
        items, addItem, removeItem, updateQty, clear, count, subtotal, originalSubtotal, savings,
        discountCents, net, open, setOpen,
        saved, saveItem, saveForLater, moveToCart, removeSaved, savedCount: saved.length,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
